import { ChevronRight } from 'lucide-react';
import { useHolidays } from '../leave/hooks.js';
import { HomeCard } from './HomeCard.js';
import { dateOnlyToDate, formatDateOnlyShort, useToday } from './useToday.js';

const DATE_FORMAT: Intl.DateTimeFormatOptions = { day: '2-digit', month: 'short', year: 'numeric' };
const SHOWN = 3;

/**
 * The next few published holidays for the caller's own location, from `GET /leave/holidays`.
 * "Upcoming" is measured from the location's today (see `useToday`), never the browser clock.
 */
export function UpcomingHolidays() {
  const today = useToday();
  const query = useHolidays();
  const all = query.data?.status === 200 ? query.data.body.items : [];
  const upcoming = today
    ? all
        .filter((h) => h.date >= today)
        .sort((a, b) => a.date.localeCompare(b.date))
        .slice(0, SHOWN)
    : [];

  return (
    <HomeCard
      title="Upcoming Holidays"
      viewAll={{ to: '/leave/holidays', label: 'View calendar' }}
      query={{ isPending: query.isPending, isError: query.isError }}
      isEmpty={upcoming.length === 0}
      emptyTitle="No upcoming holidays"
      emptyDescription="Published holidays for your location will show here."
    >
      <div className="flex flex-col gap-3">
        {upcoming.map((holiday) => (
          <div
            key={holiday.id}
            className="flex items-center gap-3 rounded-xl border border-border bg-surface p-3 shadow-xs"
          >
            <div className="flex h-9 shrink-0 items-center justify-center rounded-lg bg-accent-subtle px-2 text-[10px] font-bold text-accent">
              {formatDateOnlyShort(holiday.date, DATE_FORMAT)}
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-bold text-fg">{holiday.name}</p>
              <p className="truncate text-[10px] text-fg-subtle">
                {dateOnlyToDate(holiday.date).toLocaleDateString('en-US', {
                  weekday: 'long',
                  timeZone: 'UTC',
                })}
                {holiday.isOptional ? ' · Optional' : ''}
              </p>
            </div>
            <ChevronRight className="size-4 shrink-0 text-fg-subtle" />
          </div>
        ))}
      </div>
    </HomeCard>
  );
}
